import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import { useLanguage } from '../contexts/LanguageContext';
import { AmericanoIcon, MexicanoIcon } from './FormatIcons';
import TopActionBar from './TopActionBar';
import WhatsNew from './WhatsNew';
import {
  ALTERNATE_MEXICANO_PAIRING,
  DEFAULT_MEXICANO_PAIRING,
  normalizeMexicanoPairing,
} from '../utils/mexicano';

function TournamentForm() {
  const { translate } = useLanguage();
  const [tournamentName, setTournamentName] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialFormat = useMemo(() => {
    const fromUrl = (searchParams.get('format') || '').toLowerCase();
    if (fromUrl === 'mexicano' || fromUrl === 'americano') return fromUrl;
    return 'americano'; // default selection
  }, [searchParams]);

  const initialRoundType = useMemo(() => {
    const fromUrl = (searchParams.get('roundType') || '').toLowerCase();
    return fromUrl === 'time' ? 'time' : 'points';
  }, [searchParams]);

  const initialTargetPoints = useMemo(() => {
    const parsed = Number(searchParams.get('targetPoints'));
    return Number.isFinite(parsed) && parsed >= 1 && parsed <= 30 ? parsed : 21;
  }, [searchParams]);

  const initialRoundMinutes = useMemo(() => {
    const parsed = Number(searchParams.get('roundMinutes'));
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 12;
  }, [searchParams]);

  const initialMexicanoPairing = useMemo(
    () => normalizeMexicanoPairing(searchParams.get('mexicanoPairing')),
    [searchParams]
  );

  const [format, setFormat] = useState(initialFormat);
  const [roundType, setRoundType] = useState(initialRoundType); // 'points' | 'time'
  const [targetPoints, setTargetPoints] = useState(String(initialTargetPoints));
  const [roundMinutes, setRoundMinutes] = useState(initialRoundMinutes);
  const [mexicanoPairing, setMexicanoPairing] = useState(initialMexicanoPairing);
  const [formatError, setFormatError] = useState('');

  const persistParam = (key, value) => {
    const nextParams = new URLSearchParams(searchParams);
    if (value === '' || value === null || value === undefined) {
      nextParams.delete(key);
    } else {
      nextParams.set(key, String(value));
    }
    setSearchParams(nextParams, { replace: true });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!tournamentName.trim()) {
      setError(translate('tournament.nameRequired'));
      return;
    }
    if (!format) {
      setFormatError(translate('tournament.formatRequired'));
      return;
    }
    const params = new URLSearchParams();
    params.set('format', format);
    params.set('roundType', roundType);
    const points = Math.min(30, Math.max(1, Number(targetPoints) || 21));
    params.set('targetPoints', points);
    params.set('roundMinutes', roundMinutes);
    if (format === 'mexicano') {
      params.set('mexicanoPairing', mexicanoPairing);
    }
    navigate(`/tournament/${encodeURIComponent(tournamentName)}/setup?${params.toString()}`);
  };

  const mexicanoPairingOptions = [
    {
      value: DEFAULT_MEXICANO_PAIRING,
      title: translate('playerSetup.mexicanoPairingStandard'),
      description: translate('playerSetup.mexicanoPairingStandardDescription'),
    },
    {
      value: ALTERNATE_MEXICANO_PAIRING,
      title: translate('playerSetup.mexicanoPairingAlternate'),
      description: translate('playerSetup.mexicanoPairingAlternateDescription'),
    },
  ];

  return (
    <Box>
      <TopActionBar />
      <WhatsNew />
      <Paper sx={{ p: { xs: 3, sm: 4 } }}>
        <Typography variant="h4" gutterBottom align="center" sx={{ mb: 4 }}>
          {translate('tournament.new')}
        </Typography>
        
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label={translate('tournament.tournamentName')}
            value={tournamentName}
            onChange={(e) => {
              setTournamentName(e.target.value);
              setError('');
            }}
            error={!!error}
            helperText={error}
            margin="normal"
            sx={{ mb: 3 }}
          />
          
          <Box sx={{ mt: 4, mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
              {translate('playerSetup.roundTypeTitle')}
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={roundType}
              onChange={(_, next) => {
                if (!next) return;
                setRoundType(next);
                persistParam('roundType', next);
              }}
              fullWidth
              sx={{
                '& .MuiToggleButton-root': {
                  py: 1.5,
                  fontWeight: 700,
                },
              }}
            >
              <ToggleButton value="points">{translate('playerSetup.roundTypePoints')}</ToggleButton>
              <ToggleButton value="time">{translate('playerSetup.roundTypeTime')}</ToggleButton>
            </ToggleButtonGroup>

            {roundType === 'points' ? (
              <TextField
                fullWidth
                type="number"
                label={translate('playerSetup.targetPoints')}
                value={targetPoints}
                onChange={(e) => {
                    const raw = e.target.value;
                    // Allow clearing the input while typing.
                    if (raw === '') {
                      setTargetPoints('');
                      persistParam('targetPoints', '');
                      return;
                    }

                    const parsed = Number(raw);
                    const safe = Number.isFinite(parsed) ? parsed : 21;
                    const clamped = Math.min(30, Math.max(1, safe));
                    setTargetPoints(String(clamped));
                    persistParam('targetPoints', clamped);
                }}
                onBlur={() => {
                  // If left empty, fall back to default.
                  if (targetPoints === '') {
                    setTargetPoints('21');
                    persistParam('targetPoints', 21);
                  }
                }}
                margin="normal"
                inputProps={{ min: 1, max: 30, style: { fontWeight: 700 } }}
              />
            ) : (
              <TextField
                fullWidth
                type="number"
                label={translate('playerSetup.roundMinutes')}
                value={roundMinutes}
                onChange={(e) => {
                  const nextValue = Math.max(1, Number(e.target.value) || 0);
                  setRoundMinutes(nextValue);
                  persistParam('roundMinutes', nextValue);
                }}
                margin="normal"
                inputProps={{ min: 1, style: { fontWeight: 700 } }}
              />
            )}
          </Box>

          <Box sx={{ mt: 4, mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
              {translate('playerSetup.formatTitle')}
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={format}
              onChange={(_, next) => {
                if (!next) return;
                setFormat(next);
                setFormatError('');
                persistParam('format', next);
              }}
              fullWidth
              sx={{
                '& .MuiToggleButton-root': {
                  py: 1.5,
                },
              }}
            >
              <ToggleButton value="americano" aria-label={translate('playerSetup.formatAmericano')}>
                <Tooltip title={translate('playerSetup.formatAmericano')} placement="top" enterTouchDelay={0} leaveTouchDelay={2500} componentsProps={{ tooltip: { sx: { fontSize: '0.875rem' } } }}>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                    <AmericanoIcon size={64} />
                  </Box>
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="mexicano" aria-label={translate('playerSetup.formatMexicano')}>
                <Tooltip title={translate('playerSetup.formatMexicano')} placement="top" enterTouchDelay={0} leaveTouchDelay={2500} componentsProps={{ tooltip: { sx: { fontSize: '0.875rem' } } }}>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                    <MexicanoIcon size={64} />
                  </Box>
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
            {formatError && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {formatError}
              </Typography>
            )}

            {format === 'mexicano' && (
              <Box sx={{ mt: 2.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                  {translate('playerSetup.mexicanoPairingTitle')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {translate('playerSetup.mexicanoPairingHint')}
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                    gap: 1.5,
                  }}
                >
                  {mexicanoPairingOptions.map((option) => {
                    const isSelected = mexicanoPairing === option.value;

                    return (
                      <Paper
                        key={option.value}
                        component="button"
                        type="button"
                        elevation={0}
                        onClick={() => {
                          setMexicanoPairing(option.value);
                          persistParam('mexicanoPairing', option.value);
                        }}
                        aria-pressed={isSelected}
                        sx={{
                          p: 2,
                          width: '100%',
                          textAlign: 'left',
                          borderRadius: 2,
                          border: '2px solid',
                          borderColor: isSelected ? 'primary.main' : 'divider',
                          backgroundColor: isSelected ? 'action.selected' : 'background.paper',
                          color: 'text.primary',
                          cursor: 'pointer',
                          transition: 'border-color 0.2s ease, transform 0.2s ease, background-color 0.2s ease',
                          '&:hover': {
                            borderColor: 'primary.main',
                            transform: 'translateY(-1px)',
                          },
                        }}
                      >
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                          {option.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {option.description}
                        </Typography>
                      </Paper>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            sx={{ py: 2 }}
          >
            {translate('tournament.create')}
          </Button>
        </form>
      </Paper>
    </Box>
  );
}

export default TournamentForm;
